<template>
  <div
    class="ncrm-segmented"
    :class="{ 'ncrm-segmented--block': block, 'ncrm-segmented--disabled': disabled }"
    role="radiogroup"
    :aria-label="ariaLabel || undefined"
    :aria-disabled="disabled ? 'true' : undefined"
  >
    <button
      v-for="(item, index) in items"
      :key="item.value"
      type="button"
      role="radio"
      class="ncrm-segmented__option"
      :class="{ 'ncrm-segmented__option--active': item.value === modelValue }"
      :aria-checked="item.value === modelValue ? 'true' : 'false'"
      :tabindex="item.value === modelValue && !disabled ? 0 : -1"
      :disabled="disabled"
      @click="select(item.value)"
      @keydown="onKeydown($event, index)"
    >
      <v-icon v-if="item.icon" size="16" class="ncrm-segmented__icon">{{ item.icon }}</v-icon>
      <span class="ncrm-segmented__label">{{ item.label }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: "NcrmSegmentedControl",
  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    ariaLabel: {
      type: String,
      default: null,
    },
    // Stretch to full width and distribute options evenly (used on narrow layouts).
    block: {
      type: Boolean,
      default: false,
    },
    // Opțiunile nu mai pot fi schimbate (valoarea curentă rămâne vizibilă); ex. tip blocat după creare.
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  methods: {
    select(value) {
      if (value === this.modelValue) return;
      this.$emit("update:modelValue", value);
    },
    onKeydown(event, index) {
      const navigationKeys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];
      if (!navigationKeys.includes(event.key)) return;
      const count = this.items.length;
      if (!count) return;
      event.preventDefault();

      let nextIndex = index;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        nextIndex = (index + 1) % count;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        nextIndex = (index - 1 + count) % count;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = count - 1;
      }

      const nextItem = this.items[nextIndex];
      if (!nextItem) return;
      this.select(nextItem.value);
      this.$nextTick(() => {
        const buttons = this.$el.querySelectorAll(".ncrm-segmented__option");
        const target = buttons[nextIndex];
        if (target) target.focus();
      });
    },
  },
};
</script>

<style scoped>
/* Track-style segmented control: one cohesive surface, no floating borders.
   Use for switching between 2-4 mutually exclusive views/scopes on one surface. */
.ncrm-segmented {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
}

.ncrm-segmented__option {
  appearance: none;
  border: none;
  background: transparent;
  /* Concentric inner radius = outer radius - track padding */
  border-radius: calc(var(--app-radius-base) - 3px);
  min-height: 34px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease;
}

.ncrm-segmented__option:hover {
  color: rgb(var(--v-theme-primary));
}

.ncrm-segmented--disabled .ncrm-segmented__option {
  cursor: not-allowed;
  opacity: 0.55;
}

.ncrm-segmented__option:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
}

/* Active "thumb": raised white surface inside the muted track */
.ncrm-segmented__option--active,
.ncrm-segmented__option--active:hover {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  box-shadow: 0 1px 3px rgba(var(--v-theme-on-surface), 0.14);
}

.ncrm-segmented__icon {
  flex-shrink: 0;
}

@media (max-width: 720px) {
  .ncrm-segmented--block {
    display: flex;
    width: 100%;
  }

  .ncrm-segmented--block .ncrm-segmented__option {
    flex: 1 1 0;
    padding: 0 10px;
  }
}
</style>
